import { useState, useRef, useEffect } from "react";
import { X, Sparkles, Send, Bot, User } from "lucide-react";
import { API } from "@/lib/api";

const SUGGESTIONS = [
  "Summarize the current critical alerts",
  "Write a PowerShell script to list services that are stopped",
  "Explain Windows Event ID 6008",
  "How do I troubleshoot a device stuck pending reboot?",
];

export function CopilotDrawer({ open, setOpen }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef();

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/copilot/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = JSON.parse(line.slice(6));
          if (data.delta) setMessages((m) => { const c = [...m]; const last = c[c.length - 1]; c[c.length - 1] = { ...last, content: last.content + data.delta }; return c; });
          if (data.error) setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: "Error: " + data.error }; return c; });
        }
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1].content = "Connection error."; return c; });
    }
    setStreaming(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={() => setOpen(false)}>
      <div className="w-full max-w-md h-full flex flex-col border-l border-slate-700 shadow-2xl fade-up"
           style={{ background: "#0F1521" }} onClick={(e) => e.stopPropagation()}>
        <div className="h-14 flex items-center gap-2.5 px-4 border-b border-slate-800">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg,#00F0FF,#38BDF8)" }}>
            <Sparkles size={15} className="text-slate-950" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-[14px] text-slate-100">Control Copilot</div>
            <div className="text-[10px] text-slate-500">GPT-5.4 · advises only, never auto-executes</div>
          </div>
          <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-200"><X size={18} /></button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-slate-400">Ask about alerts, devices, scripts, Windows events, or tickets.</p>
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="copilot-suggestion"
                  className="block w-full text-left text-[13px] text-slate-300 bg-slate-800/50 hover:bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={"flex gap-2.5 " + (m.role === "user" ? "flex-row-reverse" : "")}>
              <div className={"w-7 h-7 rounded-md shrink-0 flex items-center justify-center " + (m.role === "user" ? "bg-slate-700" : "bg-cyan-500/20")}>
                {m.role === "user" ? <User size={14} className="text-slate-300" /> : <Bot size={14} className="text-cyan-300" />}
              </div>
              <div className={"rounded-lg px-3 py-2 text-[13px] leading-relaxed max-w-[85%] whitespace-pre-wrap " +
                (m.role === "user" ? "bg-cyan-500/15 text-cyan-50" : "bg-slate-800/70 text-slate-200")}>
                {m.content || <span className="text-slate-500">…</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="p-3 border-t border-slate-800">
          <div className="flex items-end gap-2 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2">
            <textarea data-testid="copilot-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1} placeholder="Ask Copilot…"
              className="flex-1 bg-transparent text-sm text-slate-100 outline-none resize-none placeholder:text-slate-500 max-h-24" />
            <button data-testid="copilot-send" onClick={() => send()} disabled={streaming}
              className="text-cyan-400 hover:text-cyan-300 disabled:opacity-40"><Send size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
